/**
 * Import Monitor — the Writeback tab's outcome table.
 *
 * Split out of ImportWritebackTab so neither file runs past the 500-line limit.
 * Everything here is presentation: which columns an outcome shows, and how a
 * before/after pair is rendered.
 */
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Loader2, CheckCircle2, Upload, Ban, ArrowRight } from 'lucide-react';
import { CUSTOMER_REQUIRED_DATE } from '../../config/dateLabels';
import type { WritebackRow } from '../../lib/api/imports';

export type Outcome = 'pending' | 'succeeded' | 'failed' | 'cancelled';

/** How long a writeback may sit unsent before the queue counts as stalled. */
export const STALE_PENDING_HOURS = 6;

function formatValue(value: string | number | null, field: WritebackRow['field']): string {
  if (value === null || value === undefined || value === '') return '—';
  // Either spelling: the API moved to the canonical name in PR 3 and the old
  // one is still accepted for one release.
  if (field === 'customer_required_date' || field === 'delivery_date') {
    const d = new Date(String(value));
    return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-GB');
  }
  const num = Number(value);
  return isNaN(num) ? String(value) : num.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatWhen(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

function ageInHours(value: string | null): number | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / 3600000;
}

function FieldBadge({ row }: { row: WritebackRow }) {
  if (!row.field) {
    return <span className="text-xs text-muted-foreground">Acknowledgement</span>;
  }
  const label = (row.field === 'customer_required_date' || row.field === 'delivery_date')
    ? CUSTOMER_REQUIRED_DATE
    : row.field === 'unit_price' ? 'Unit price'
    : 'Quantity';
  return <span className="text-xs font-medium">{label}</span>;
}

/**
 * The before -> after pair. Acknowledgements (supplier accepted / declined)
 * change no value, so they render a dash rather than an empty arrow that
 * would read like missing data.
 */
function ValueChange({ row }: { row: WritebackRow }) {
  if (!row.field) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 tabular-nums">
      <span className="text-muted-foreground">{formatValue(row.previous_value, row.field)}</span>
      <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
      <span className="font-medium">{formatValue(row.current_value, row.field)}</span>
    </span>
  );
}

/**
 * The ERP row key, with the divergence called out.
 *
 * The portal line number is a display position and the ERP row key is not —
 * they agree on an untouched document and drift apart once a line is deleted
 * or re-sequenced. A row where they disagree is the one worth looking at,
 * because that is where a writeback keyed off the display position lands on
 * the wrong part, so it is flagged rather than left for the reader to spot by
 * subtracting one in their head.
 */
function SapRowCell({ row }: { row: WritebackRow }) {
  if (row.sap_order_row === null || row.sap_order_row === undefined) {
    return (
      <td className="py-2.5 pr-4 text-muted-foreground" title="Line imported before the ERP row key was captured.">
        —
      </td>
    );
  }
  const portalPosition = Number(row.line_number);
  const diverged =
    Number.isFinite(portalPosition) && portalPosition - 1 !== row.sap_order_row;
  return (
    <td
      className={`py-2.5 pr-4 tabular-nums whitespace-nowrap ${diverged ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}
      title={
        diverged
          ? `ERP row ${row.sap_order_row} does not match portal line ${row.line_number}. The ERP addresses this row by ${row.sap_order_row}.`
          : undefined
      }
    >
      {row.sap_order_row}
      {diverged && ' ⚠'}
    </td>
  );
}

export function WritebackTable({
  rows, outcome, truncated, onCancel, cancelling, selected, onToggle, onToggleAll,
  showSapRow = false,
}: {
  rows: WritebackRow[];
  outcome: Outcome;
  truncated: boolean;
  onCancel?: (row: WritebackRow) => void;
  cancelling?: number | null;
  selected?: Set<number>;
  onToggle?: (id: number) => void;
  onToggleAll?: (ids: number[]) => void;
  /**
   * Show the ERP row key beside PO / Line. Driven by the platform setting
   * `writeback_show_sap_row`; off for everyone by default.
   */
  showSapRow?: boolean;
}) {
  const selectable = !!(onToggle && selected);
  // Only what is on screen can be select-all'd — the list is capped at 200,
  // and a header checkbox that silently meant "and the 1,096 you cannot see"
  // would be a trap on a destructive action.
  const visibleIds = rows.map(r => r.id);
  const allSelected = selectable && visibleIds.length > 0
    && visibleIds.every(id => selected!.has(id));
  if (rows.length === 0) {
    const empty = outcome === 'pending'
      ? { icon: CheckCircle2, tone: 'text-emerald-500', title: 'Nothing waiting', sub: 'Every change has been sent to the ERP' }
      : outcome === 'succeeded'
      ? { icon: Upload, tone: 'text-slate-400', title: 'No writebacks completed', sub: 'Nothing landed in the ERP in this window' }
      : outcome === 'cancelled'
      ? { icon: Ban, tone: 'text-slate-400', title: 'Nothing cancelled', sub: 'No writebacks were withdrawn in this window' }
      : { icon: CheckCircle2, tone: 'text-emerald-500', title: 'No failures', sub: 'Every writeback in this window was accepted' };
    const Icon = empty.icon;
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Icon className={`h-10 w-10 ${empty.tone} mb-3`} />
        <p className="text-sm font-medium">{empty.title}</p>
        <p className="text-xs text-muted-foreground mt-1">{empty.sub}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
            {selectable && (
              <th className="py-2 pr-3 w-8">
                <input
                  type="checkbox"
                  aria-label="Select all shown"
                  className="w-3.5 h-3.5 accent-primary cursor-pointer"
                  checked={allSelected}
                  onChange={() => onToggleAll?.(visibleIds)}
                />
              </th>
            )}
            <th className="py-2 pr-4 font-medium">PO / Line</th>
            {showSapRow && (
              <th className="py-2 pr-4 font-medium" title="The ERP's own row key. Differs from the portal line number when a line has been deleted or re-sequenced in the ERP.">
                SAP Row
              </th>
            )}
            <th className="py-2 pr-4 font-medium">Supplier</th>
            <th className="py-2 pr-4 font-medium">Change</th>
            <th className="py-2 pr-4 font-medium">Field</th>
            <th className="py-2 pr-4 font-medium">Value</th>
            <th className="py-2 pr-4 font-medium">Raised</th>
            <th className="py-2 pr-4 font-medium">
              {outcome === 'pending' ? 'Waiting'
                : outcome === 'succeeded' ? 'Written back'
                : outcome === 'cancelled' ? 'Cancelled at' : 'Failed at'}
            </th>
            {outcome === 'pending' && <th className="py-2 pr-4 font-medium">Attempts</th>}
            {outcome === 'pending' && onCancel && <th className="py-2 pr-4 font-medium"></th>}
            {outcome === 'failed' && <th className="py-2 pr-4 font-medium">Reason</th>}
            {outcome === 'cancelled' && <th className="py-2 pr-4 font-medium">Withdrawn by</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const waiting = outcome === 'pending' ? ageInHours(row.raised_at) : null;
            const stale = waiting !== null && waiting >= STALE_PENDING_HOURS;
            return (
              <tr key={`${outcome}-${row.id}`} className={`border-b last:border-0 hover:bg-muted/40 ${selectable && selected!.has(row.id) ? 'bg-primary/5' : ''}`}>
                {selectable && (
                  <td className="py-2.5 pr-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.po_number} line ${row.line_number}`}
                      className="w-3.5 h-3.5 accent-primary cursor-pointer"
                      checked={selected!.has(row.id)}
                      onChange={() => onToggle?.(row.id)}
                    />
                  </td>
                )}
                <td className="py-2.5 pr-4 font-medium whitespace-nowrap">
                  {row.po_number} <span className="text-muted-foreground">/ {row.line_number}</span>
                </td>
                {showSapRow && <SapRowCell row={row} />}
                <td className="py-2.5 pr-4 text-muted-foreground">{row.supplier_name || '—'}</td>
                <td className="py-2.5 pr-4 whitespace-nowrap">{row.change_type_display}</td>
                <td className="py-2.5 pr-4 whitespace-nowrap"><FieldBadge row={row} /></td>
                <td className="py-2.5 pr-4 whitespace-nowrap"><ValueChange row={row} /></td>
                <td className="py-2.5 pr-4 text-muted-foreground whitespace-nowrap">{formatWhen(row.raised_at)}</td>
                <td className="py-2.5 pr-4 whitespace-nowrap">
                  {outcome === 'pending' ? (
                    <span className={stale ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
                      {waiting === null ? '—'
                        : waiting < 1 ? `${Math.round(waiting * 60)}m`
                        : waiting < 24 ? `${waiting.toFixed(1)}h`
                        : `${Math.floor(waiting / 24)}d`}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{formatWhen(row.timestamp)}</span>
                  )}
                </td>
                {outcome === 'pending' && (
                  <td className="py-2.5 pr-4 tabular-nums">
                    {row.try_times && row.try_times > 0 ? (
                      <Badge variant="outline" className="text-amber-700 border-amber-200">{row.try_times}</Badge>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                )}
                {outcome === 'pending' && onCancel && (
                  <td className="py-2.5 pr-4 whitespace-nowrap">
                    <Button
                      variant="outline" size="sm"
                      className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                      disabled={cancelling === row.id}
                      onClick={() => onCancel(row)}
                    >
                      {cancelling === row.id
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : 'Cancel'}
                    </Button>
                  </td>
                )}
                {outcome === 'failed' && (
                  <td className="py-2.5 pr-4 max-w-md">
                    <p className="text-xs font-medium text-red-700">{row.failed_type_display}</p>
                    {row.error && <p className="text-xs text-muted-foreground truncate">{row.error}</p>}
                  </td>
                )}
                {outcome === 'cancelled' && (
                  <td className="py-2.5 pr-4 max-w-xs">
                    <p className="text-xs">{row.cancelled_by || '—'}</p>
                    {row.cancel_reason && (
                      <p className="text-xs text-muted-foreground truncate">{row.cancel_reason}</p>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {truncated && (
        <p className="text-xs text-muted-foreground mt-3">
          Showing the first 200 rows — the counts above are the full totals.
        </p>
      )}
    </div>
  );
}

